import { validateNumeric2DArray, validateArray, validateShapesMatch, validateFitted } from '../utils/validation.js';

export class LogisticRegression {
  constructor(options = {}) {
    this.learningRate = options.learningRate || 0.01;
    this.maxIterations = options.maxIterations || 1000;
    this.tolerance = options.tolerance || 0.0001;
    this.fitIntercept = options.fitIntercept !== undefined ? options.fitIntercept : true;
    this.multiClass = options.multiClass || 'ovr';

    this.coefficients = null;
    this.intercept = null;
    this.classes = null;
    this.isFitted = false;
    this.models = null;
  }

  sigmoid(z) {
    return z.map(val => 1 / (1 + Math.exp(-val)));
  }

  softmax(z) {
    const exp = z.map(row => row.map(val => Math.exp(val - Math.max(...row))));
    return exp.map(row => {
      const sum = row.reduce((a, b) => a + b, 0);
      return row.map(val => val / sum);
    });
  }

  fit(X, y) {
    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');
    validateArray(y, 'y');
    validateShapesMatch(X, y);

    this.classes = Array.from(new Set(y)).sort();

    if (this.classes.length === 2) {
      this._fitBinary(X, y);
    } else {
      this._fitMulticlass(X, y);
    }

    this.isFitted = true;
    return this;
  }

  _fitBinary(X, y) {
    const nSamples = X.length;
    const nFeatures = X[0].length;

    const yBinary = y.map(val => val === this.classes[1] ? 1 : 0);

    this.coefficients = Array(nFeatures).fill(0);
    this.intercept = 0;

    for (let iter = 0; iter < this.maxIterations; iter++) {
      const z = X.map((row, i) => {
        let sum = this.intercept;
        for (let j = 0; j < nFeatures; j++) {
          sum += row[j] * this.coefficients[j];
        }
        return sum;
      });

      const predictions = this.sigmoid(z);

      const gradients = Array(nFeatures).fill(0);
      let interceptGradient = 0;

      for (let i = 0; i < nSamples; i++) {
        const error = predictions[i] - yBinary[i];
        for (let j = 0; j < nFeatures; j++) {
          gradients[j] += error * X[i][j];
        }
        interceptGradient += error;
      }

      const oldCoefficients = [...this.coefficients];

      for (let j = 0; j < nFeatures; j++) {
        this.coefficients[j] -= this.learningRate * gradients[j] / nSamples;
      }

      if (this.fitIntercept) {
        this.intercept -= this.learningRate * interceptGradient / nSamples;
      }

      const diff = oldCoefficients.reduce((sum, val, i) =>
        sum + Math.abs(val - this.coefficients[i]), 0);

      if (diff < this.tolerance) {
        break;
      }
    }
  }

  _fitMulticlass(X, y) {
    const nClasses = this.classes.length;

    if (this.multiClass === 'ovr') {
      this.models = [];

      for (const targetClass of this.classes) {
        // Convert to binary labels (1 for target class, 0 for all others)
        const yBinary = y.map(val => val === targetClass ? 1 : 0);
        const model = new LogisticRegression({
          learningRate: this.learningRate,
          maxIterations: this.maxIterations,
          tolerance: this.tolerance,
          fitIntercept: this.fitIntercept
        });
        model.fit(X, yBinary);
        this.models.push(model);
      }
    } else {
      const nFeatures = X[0].length;
      const nSamples = X.length;

      this.coefficients = Array(nClasses).fill(0).map(() => Array(nFeatures).fill(0));
      this.intercept = Array(nClasses).fill(0);

      for (let iter = 0; iter < this.maxIterations; iter++) {
        const z = X.map(row => {
          return this.coefficients.map((coef, k) => {
            let sum = this.intercept[k];
            for (let j = 0; j < nFeatures; j++) {
              sum += row[j] * coef[j];
            }
            return sum;
          });
        });

        const probabilities = this.softmax(z);

        // Store old coefficients for convergence check
        const oldCoefficients = this.coefficients.map(row => [...row]);
        const oldIntercept = [...this.intercept];

        for (let k = 0; k < nClasses; k++) {
          const gradients = Array(nFeatures).fill(0);
          let interceptGradient = 0;

          for (let i = 0; i < nSamples; i++) {
            const error = probabilities[i][k] - (y[i] === this.classes[k] ? 1 : 0);
            for (let j = 0; j < nFeatures; j++) {
              gradients[j] += error * X[i][j];
            }
            interceptGradient += error;
          }

          for (let j = 0; j < nFeatures; j++) {
            this.coefficients[k][j] -= this.learningRate * gradients[j] / nSamples;
          }

          if (this.fitIntercept) {
            this.intercept[k] -= this.learningRate * interceptGradient / nSamples;
          }
        }

        // Check for convergence
        let maxDiff = 0;
        for (let k = 0; k < nClasses; k++) {
          for (let j = 0; j < nFeatures; j++) {
            maxDiff = Math.max(maxDiff, Math.abs(this.coefficients[k][j] - oldCoefficients[k][j]));
          }
          if (this.fitIntercept) {
            maxDiff = Math.max(maxDiff, Math.abs(this.intercept[k] - oldIntercept[k]));
          }
        }

        if (maxDiff < this.tolerance) {
          break;
        }
      }
    }
  }

  predict(X) {
    validateFitted(this.isFitted, 'LogisticRegression');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    const probabilities = this.predictProba(X);

    return probabilities.map(probs => {
      const maxIndex = probs.indexOf(Math.max(...probs));
      return this.classes[maxIndex];
    });
  }

  predictProba(X) {
    validateFitted(this.isFitted, 'LogisticRegression');

    if (!Array.isArray(X[0])) {
      X = X.map(val => [val]);
    }

    validateNumeric2DArray(X, 'X');

    if (this.classes.length === 2) {
      const z = X.map(row => {
        let sum = this.intercept;
        for (let j = 0; j < row.length; j++) {
          sum += row[j] * this.coefficients[j];
        }
        return sum;
      });

      const prob1 = this.sigmoid(z);
      return prob1.map(p => [1 - p, p]);
    }

    if (this.models) {
      const scores = X.map(row => {
        return this.models.map(model => {
          const proba = model.predictProba([row])[0];
          return proba[1];
        });
      });

      return scores.map(row => {
        const sum = row.reduce((a, b) => a + b, 0) || 1;
        return row.map(val => val / sum);
      });
    }

    const z = X.map(row => {
      return this.coefficients.map((coef, k) => {
        let sum = this.intercept[k];
        for (let j = 0; j < row.length; j++) {
          sum += row[j] * coef[j];
        }
        return sum;
      });
    });

    return this.softmax(z);
  }

  score(X, y) {
    validateFitted(this.isFitted, 'LogisticRegression');

    const predictions = this.predict(X);
    const correct = predictions.filter((pred, i) => pred === y[i]).length;
    return correct / y.length;
  }
}
